import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getIntakeReadiness } from '@/services/intake.service.js';

// Both thresholds (READY TO CLEAR / READY TO QUOTE) still compute server-side
// and gate dispatch. The DISPLAY (WALKTHROUGH fix 3) collapses to ONE tag:
//   quote ready       -> green  "Ready to quote"
//   clear ready only  -> amber  "Ready to clear, N to quote" (quote gaps on expand)
//   not clear ready   -> red    "Not ready to clear" (clear gaps shown inline)
export function readinessTag(r) {
  if (r?.quote?.ready) {
    return { label: 'Ready to quote', cls: 'bg-success-soft text-success', list: [], expandable: false };
  }
  if (r?.clear?.ready) {
    const n = (r.quote?.missing || []).length;
    return { label: `Ready to clear, ${n} to quote`, cls: 'bg-warning-soft text-warning', list: r.quote?.missing || [], expandable: true };
  }
  return { label: 'Not ready to clear', cls: 'bg-accent-soft text-accent', list: r?.clear?.missing || [], expandable: false };
}

// MV-01 3.3 (Ship 1b): the intake readiness meter on the deal workspace.
// Readiness is DERIVED server-side (computeIntakeReadiness, including the
// mid-intake draft overlay); this only renders it. The endpoint returns
// readiness:null for deals with no intake session (renewals, legacy).
//
// EVERY BLOCKER CAN NOW CARRY ITS OWN FIX (prompt 98 item 2). A readiness item
// is an object with a stable code and an optional `remedy`, and the remedy names
// an EXISTING door: `remedy.opener` is a useDealActions openers key. One is
// wired today (the address) and the rest render exactly as they did, which is
// the point — the shape is a refactor, the doors arrive one at a time.
// CLIENT-RECORD-SPEC §5.1 (David: both, linked): the readiness list keeps its
// severity contract and gains ONE link to the record with the Incomplete
// filter on, where every empty required fact is a field in place. The
// record's fact_ref is what pairs a blocker with its row; this surface does
// not re-derive anything.
export default function IntakeReadinessCard({ dealId, refreshKey, openers, accountId }) {
  const [readiness, setReadiness] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    let dead = false;
    getIntakeReadiness(dealId)
      .then((d) => { if (!dead) setReadiness(d.readiness || null); })
      .catch(() => { if (!dead) setReadiness(null); });
    return () => { dead = true; };
  }, [dealId, refreshKey]);

  if (!readiness) return null;

  const tag = readinessTag(readiness);
  // Red shows its clear gaps inline; amber hides quote gaps behind the expander.
  const listShown = tag.expandable ? open : tag.list.length > 0;

  return (
    <div>
      <button
        type="button"
        disabled={!tag.expandable}
        onClick={() => tag.expandable && setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${tag.cls} ${tag.expandable ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {tag.label}
        {tag.expandable && <span className="opacity-70">{open ? '▾' : '▸'}</span>}
      </button>
      {/* THE carried_forward reader (Prompt 166 §4): the renewal seed writes
          the expiring term's facts marked NOT attested, and a value marked
          "not attested" that no surface renders is treated as attested by
          every surface. This line is the distinction, on the broker's one
          look at intake state. It disappears when the client confirms. */}
      {readiness.carried_forward ? (
        <p className="mt-1 text-[11px] text-warning">
          Seeded from {readiness.carried_forward.from_deal_name || 'the expiring term'}: these are the
          policy&rsquo;s current facts, not the client&rsquo;s answers. Unconfirmed until they complete Tower.
        </p>
      ) : null}
      {listShown && tag.list.length > 0 && accountId ? (
        <Link to={`/account/${encodeURIComponent(accountId)}?incomplete=1`} className="mt-1 block text-[11px] text-text-muted underline-offset-2 hover:text-text hover:underline" data-record-link>
          Fix these on the client record
        </Link>
      ) : null}
      {listShown && tag.list.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px] leading-snug text-text-muted">
          {tag.list.map((m, i) => {
            // A blocker with a remedy renders the control beside the sentence.
            // The opener must actually exist on this surface: a button that
            // names a door and then does nothing is the defect this whole item
            // is about, one layer down.
            const open = m?.remedy?.opener ? openers?.[m.remedy.opener] : null;
            return (
              <li key={m?.code || i} className="flex flex-wrap items-baseline gap-x-1.5">
                <span>{m?.message ?? m}</span>
                {open ? (
                  <button
                    type="button"
                    onClick={() => open()}
                    className="rounded border border-border px-1.5 py-px text-[10.5px] text-text-muted hover:border-border-strong hover:text-text"
                  >
                    {m.remedy.label || 'Fix'}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
